<script lang="ts">
  import { onMount } from 'svelte';
  import type { Lawyer } from '@domain/lawyer/model';
  import { patchLawyer } from '@entities/lawyer/store.svelte';
  import { lawyerPositionsApi } from '@shared/api/lawyer-positions';
  import {
    DISPLAY_STATUS_BADGE_CLS,
    DISPLAY_STATUS_LABELS,
  } from '@shared/constants/sales-action-display';
  import type { CustomerStatusDisplay } from '@shared/types/firm';
  import InlineField from '@shared/ui/InlineField.svelte';
  import { formatDateYmd } from '@shared/util/format';
  import {
    BULK_SALES_STATE_LABELS,
    lawyerBulkSalesTooltipLines,
  } from '@domain/bulk-sales/labels';

  interface Props {
    lawyers: Lawyer[];
    /// 代表弁護士 (0..N) の id (spec 20260612-multiple-firm-representative-lawyers)。
    /// `is_representative` 派生値の補完用 (firm detail 由来の保存値)。
    representativeIds: string[];
    canEdit: boolean;
  }

  let { lawyers, representativeIds, canEdit }: Props = $props();

  // 役職をインライン保存した後の値を弁護士 id 単位で保持し、 該当行の表示を即時
  // 更新する (InlineField は value prop 由来で表示するため、 行データの更新が必要)。
  // 親が lawyers を再取得した場合もサーバ値と一致するため矛盾しない。
  let positionOverrides = $state<Record<string, string | null>>({});

  // 役職選択肢は役職マスタ (GET /api/lawyer-positions) から取得する
  // (LawyerDetail と同じ single source。 value = label = name)。
  let positionOptions = $state<Array<{ value: string; label: string }>>([]);
  onMount(async () => {
    try {
      const positions = await lawyerPositionsApi.list();
      positionOptions = positions.items.map((p) => ({ value: p.name, label: p.name }));
    } catch {
      positionOptions = [];
    }
  });

  function isRep(l: Lawyer): boolean {
    return l.is_representative || representativeIds.includes(l.id);
  }

  function fullName(l: Lawyer): string {
    return l.full_name.trim();
  }

  function positionOf(l: Lawyer): string | null {
    const override: string | null | undefined = positionOverrides[l.id];
    return override === undefined ? l.position : override;
  }

  // 役職をインライン保存する。 成功時のみ override を更新して該当行を更新し、
  // 失敗時は throw して InlineField 側で元の値に戻し + エラー表示させる。
  // backend の position 更新は COALESCE で `null` を「変更なし」 と扱い解除でき
  // ないため、 select は allowEmpty=false で「(未設定)」 を出さず、 ここには
  // 必ず非空の役職が渡る (backend 非変更の制約 / brief の未設定UIは任意)。
  async function savePosition(id: string, next: string): Promise<void> {
    const updated = await patchLawyer(id, { position: next });
    positionOverrides = { ...positionOverrides, [id]: updated.position };
  }

  // ステータス / 最終接触は弁護士一覧 API の list-only 集計 (弁護士単位の
  // proposal_summary / last_contact_at) 由来。接点ゼロは 未接点 / 未接触。
  function statusOf(l: Lawyer): CustomerStatusDisplay {
    return l.proposal_summary?.representative_status ?? 'no_contact';
  }

  // backend が集計時に確定させた representative_label を正とする。
  function statusLabel(l: Lawyer): string {
    return l.proposal_summary?.representative_label ?? DISPLAY_STATUS_LABELS.no_contact;
  }

  // 値なし = 接触履歴ゼロは「未接触」。不正値は formatDateYmd が — を返す。
  function lastContactDisplay(l: Lawyer): string {
    if (!l.last_contact_at) return '未接触';
    return formatDateYmd(l.last_contact_at);
  }

  // 一括営業列 (spec 20260716「法律事務所詳細の所属弁護士一覧」)。弁護士一覧 API の
  // 派生値 (本人 / 現所属事務所 / 懲戒の OR 合成。 行ごとの追加取得なし) を 3 段階バッジで
  // 描画し、 ツールチップに対象外チャネル・連絡経路・由来を出す。 編集操作は置かない。
  function bulkSalesTooltipId(l: Lawyer): string {
    return `bulk-sales-tooltip-${l.id}`;
  }

  // ツールチップは `.table-scroll` (overflow-x: auto → overflow-y も auto) の内側にあるため、
  // 下端の行では絶対配置だとスクロール領域に切られる。表示直前 (ホバー / フォーカス開始) に
  // バッジの位置から fixed 座標を与えて overflow の外へ出す。表示自体は CSS (:hover /
  // :focus-within) が担う。viewport 下端に収まらないときはバッジの上へ反転する
  // (所属弁護士タブの末尾行はドキュメント最下部に来うる)。
  const BULK_SALES_TOOLTIP_GAP_PX = 4;
  // 表示前で高さが取れないときの見積もり (見出し + 最大 7 行)。
  const BULK_SALES_TOOLTIP_FALLBACK_HEIGHT_PX = 170;
  function placeBulkSalesTooltip(ev: Event): void {
    const anchor = ev.currentTarget as HTMLElement;
    const tooltip = anchor.querySelector<HTMLElement>('.bulk-sales-tooltip');
    if (!tooltip) return;
    const rect = anchor.getBoundingClientRect();
    const height = tooltip.offsetHeight || BULK_SALES_TOOLTIP_FALLBACK_HEIGHT_PX;
    const below = rect.bottom + BULK_SALES_TOOLTIP_GAP_PX;
    const fitsBelow = below + height <= window.innerHeight;
    const above = rect.top - BULK_SALES_TOOLTIP_GAP_PX - height;
    tooltip.style.top = `${fitsBelow || above < 0 ? below : above}px`;
    tooltip.style.right = `${Math.max(8, window.innerWidth - rect.right)}px`;
  }
</script>

<section>
  <header class="header">
    <h3>所属弁護士 ({lawyers.length}件)</h3>
  </header>
  {#if lawyers.length === 0}
    <p class="muted empty">まだ弁護士が登録されていません。</p>
  {:else}
    <div class="table-scroll">
      <!--
        氏名 / 役職 / 修習期 / ステータス / 最終接触 の列構成。
        役職は役職マスタ (GET /api/lawyer-positions) を参照する inline select
        で編集できる。 修習期は保存値 (例: 77期 / 65期) がそのまま表示ラベル
        のため、 UI 側で「期」 を付与しない (値なしは —)。
        ステータス / 最終接触は弁護士一覧 API の list-only 集計 (弁護士単位の
        proposal_summary / last_contact_at) 由来。 接点ゼロは 未接点 / 未接触。
      -->
      <table>
        <thead>
          <tr>
            <th>氏名</th>
            <th>役職</th>
            <th>修習期</th>
            <th>ステータス</th>
            <th>最終接触</th>
            <th>一括営業</th>
          </tr>
        </thead>
        <tbody>
          {#each lawyers as lawyer (lawyer.id)}
            {@const status = statusOf(lawyer)}
            {@const decision = lawyer.bulk_sales_decision}
            <tr class:representative-row={isRep(lawyer)}>
              <td>
                <a href={`/lawyers/${lawyer.id}`}>{fullName(lawyer)}</a>
                {#if isRep(lawyer)}
                  <span class="representative-badge">代表</span>
                {/if}
              </td>
              <td class="position-cell">
                <InlineField
                  value={positionOf(lawyer)}
                  type="select"
                  options={positionOptions}
                  allowEmpty={false}
                  disabled={!canEdit}
                  onSave={(next) => savePosition(lawyer.id, next)}
                />
              </td>
              <td>{lawyer.legal_training_term ?? '—'}</td>
              <td class="status-cell">
                <span class={`status-chip ${DISPLAY_STATUS_BADGE_CLS[status]}`}>
                  {statusLabel(lawyer)}
                </span>
              </td>
              <td class="last-contact-cell">
                {lastContactDisplay(lawyer)}
              </td>
              <td class="bulk-sales-cell">
                <span
                  class="bulk-sales-anchor"
                  role="presentation"
                  onmouseenter={placeBulkSalesTooltip}
                  onfocusin={placeBulkSalesTooltip}
                >
                  <!-- ホバーだけでなくキーボードフォーカスでもツールチップを出すため、
                       フォーカス可能な button にする (aria-describedby で内容を結ぶ)。
                       押しても何も起きない (設定変更は詳細画面の管轄)。 -->
                  <button
                    type="button"
                    class="bulk-sales-badge"
                    class:bulk-sales-partial={decision.state === 'partially_excluded'}
                    class:bulk-sales-excluded={decision.state === 'excluded'}
                    aria-describedby={bulkSalesTooltipId(lawyer)}
                    data-testid="bulk-sales-badge"
                  >
                    {BULK_SALES_STATE_LABELS[decision.state]}
                  </button>
                  <span
                    class="bulk-sales-tooltip"
                    role="tooltip"
                    id={bulkSalesTooltipId(lawyer)}
                  >
                    <span class="bulk-sales-tooltip-title">対象外チャネルと由来</span>
                    {#each lawyerBulkSalesTooltipLines(decision) as line (line)}
                      <span class="bulk-sales-tooltip-line">{line}</span>
                    {/each}
                  </span>
                </span>
              </td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>
  {/if}
</section>

<style>
  section {
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    padding: 16px;
  }
  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
  }
  h3 {
    margin: 0;
    font-size: 0.875rem;
    color: #374151;
  }
  .table-scroll {
    overflow-x: auto;
  }
  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.875rem;
    min-width: 760px;
  }
  th,
  td {
    text-align: left;
    padding: 8px 10px;
    border-bottom: 1px solid #e5e7eb;
  }
  th {
    color: #4b5563;
    background: #f9fafb;
    font-weight: 600;
    font-size: 0.75rem;
  }
  td a {
    color: #1d4ed8;
    text-decoration: none;
  }
  td a:hover {
    text-decoration: underline;
  }
  /* 役職セルは inline select が窮屈にならない最小幅を確保する。 */
  .position-cell {
    min-width: 140px;
  }
  .status-cell {
    min-width: 104px;
    white-space: nowrap;
  }
  .last-contact-cell {
    min-width: 112px;
    white-space: nowrap;
  }
  .bulk-sales-cell {
    min-width: 104px;
    white-space: nowrap;
  }
  .bulk-sales-anchor {
    position: relative;
    display: inline-block;
  }
  /* 3 段階バッジ: 対象 = 薄いグレー / 一部対象外 = 黄 / 対象外 = 赤。 */
  .bulk-sales-badge {
    display: inline-block;
    padding: 2px 8px;
    border: 0;
    border-radius: 999px;
    font: inherit;
    font-size: 0.75rem;
    font-weight: 600;
    line-height: 1.4;
    white-space: nowrap;
    background: #f3f4f6;
    color: #4b5563;
    cursor: default;
  }
  .bulk-sales-badge.bulk-sales-partial {
    background: #fef3c7;
    color: #92400e;
  }
  .bulk-sales-badge.bulk-sales-excluded {
    background: #fee2e2;
    color: #991b1b;
  }
  .bulk-sales-badge:focus-visible {
    outline: 2px solid #2563eb;
    outline-offset: 2px;
  }
  .bulk-sales-tooltip {
    display: none;
    position: fixed;
    z-index: 20;
    min-width: 180px;
    padding: 8px 10px;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    background: #fff;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
    color: #111827;
    font-size: 0.75rem;
    font-weight: 400;
    white-space: nowrap;
    text-align: left;
  }
  .bulk-sales-anchor:hover .bulk-sales-tooltip,
  .bulk-sales-anchor:focus-within .bulk-sales-tooltip {
    display: grid;
    gap: 2px;
  }
  .bulk-sales-tooltip-title {
    color: #6b7280;
    font-weight: 600;
  }
  /* ステータスのピルは営業アクションタブ (ResultActionTable) の status-chip と
     同一の見た目・パレットに揃える。 クラス名は shared/constants/
     sales-action-display.ts の DISPLAY_STATUS_BADGE_CLS に対応。 */
  .status-chip {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 0.75rem;
    font-weight: 500;
    white-space: nowrap;
  }
  .status-no-contact {
    background: #f3f4f6;
    color: #4b5563;
  }
  .status-contacted {
    background: #dbeafe;
    color: #1e40af;
  }
  .status-in-dialogue {
    background: #e0f2fe;
    color: #075985;
  }
  .status-appointment {
    background: #fef3c7;
    color: #92400e;
  }
  .status-negotiation-c {
    background: #ffedd5;
    color: #9a3412;
  }
  .status-negotiation-b {
    background: #dcfce7;
    color: #166534;
  }
  .status-negotiation-a {
    background: #bbf7d0;
    color: #166534;
  }
  .status-won {
    background: #dcfce7;
    color: #166534;
  }
  .status-lost {
    background: #fee2e2;
    color: #991b1b;
  }
  .muted {
    color: #6b7280;
  }
  .empty {
    font-size: 0.875rem;
    margin: 8px 0 0;
  }
  .representative-row {
    background: #fffbeb;
  }
  /* 複数代表でも個々の行で判別できるよう氏名横にバッジを出す
     (spec 20260612-multiple-firm-representative-lawyers)。 */
  .representative-badge {
    display: inline-block;
    margin-left: 6px;
    padding: 0 6px;
    border: 1px solid #f59e0b;
    border-radius: 9999px;
    background: #fef3c7;
    color: #92400e;
    font-size: 0.6875rem;
    line-height: 1.4;
    vertical-align: middle;
  }
</style>
